import {FC, useEffect, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import Swal from 'sweetalert2'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Button} from '../../../components/Button/Button'
import {Datatable} from '../../../components/Datatable/Datatable'
import {Loading} from '../../../components/Loading/Loading'
import {DELETE, GET} from '../../../services/api'
import useAuth from '../../../hooks/useAuth'

type NoSaleReasonCodeItem = {
  id: number
  slugSaleReasonCode: string
  saleReasonCode: string
  created?: string
}

const normalizeApiData = <T,>(response: any): T => {
  if (response && typeof response === 'object' && 'data' in response) {
    return response.data as T
  }

  return response as T
}

const SellerNoSaleReasonContent: FC = () => {
  const navigate = useNavigate()
  const {loginInfo} = useAuth()

  const canDelete = ['owner', 'admin'].includes((loginInfo?.profile || '').toLowerCase())

  const [loading, setLoading] = useState(false)
  const [reasons, setReasons] = useState<NoSaleReasonCodeItem[]>([])
  const [descriptionFilter, setDescriptionFilter] = useState('')

  const normalizedFilter = (value: string) =>
    value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()

  const formattedData = useMemo(() => {
    const normalizedDescriptionFilter = normalizedFilter(descriptionFilter)

    return reasons
      .filter((item) => {
        const normalizedDescription = normalizedFilter(item.saleReasonCode || '')

        const matchDescription =
          !normalizedDescriptionFilter ||
          normalizedDescription.includes(normalizedDescriptionFilter)

        return matchDescription
      })
      .sort((a, b) => {
        const aTime = a.created ? new Date(a.created).getTime() : 0
        const bTime = b.created ? new Date(b.created).getTime() : 0
        return bTime - aTime
      })
      .map((item) => {
      const createdAt = item.created ? new Date(item.created).toLocaleString('pt-BR') : '-'

      return {
        ...item,
        createdAt,
      }
    })
  }, [descriptionFilter, reasons])

  const loadReasons = async () => {
    setLoading(true)
    try {
      const response = await GET('/seller-no-sale-reason')
      setReasons(normalizeApiData<NoSaleReasonCodeItem[]>(response) || [])
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: String(error || 'Não foi possível carregar os motivos'),
      })
      setReasons([])
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: number) => {
    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'Excluir motivo',
      text: 'Deseja realmente excluir este motivo?',
      showCancelButton: true,
      confirmButtonText: 'Sim',
      cancelButtonText: 'Cancelar',
    })

    if (!confirm.isConfirmed) {
      return
    }

    setLoading(true)
    try {
      await DELETE(`/seller-no-sale-reason/${id}`)
      await loadReasons()
      Swal.fire({icon: 'success', title: 'Sucesso', text: 'Motivo excluído com sucesso'})
    } catch (error: any) {
      Swal.fire({icon: 'error', title: 'Erro', text: String(error || 'Falha ao excluir')})
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadReasons()
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5 d-flex justify-content-between align-items-center'>
              <h3 className='card-title fw-bolder fs-3 mb-0'>Motivos de Não Venda</h3>
              <Button
                text='Novo Motivo'
                color='primary'
                iconColor='#FFF'
                icon='fas fa-plus'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={() => navigate('/seller/reason/create')}
                btnClass='fw-bolder'
              />
            </div>

            <div className='card-body'>
              <div className='row g-3 mb-6'>
                <div className='col-md-10'>
                  <label className='form-label'>Filtrar por Descrição</label>
                  <input
                    className='form-control'
                    placeholder='Ex.: Cliente não encontrou tamanho'
                    value={descriptionFilter}
                    onChange={(e) => setDescriptionFilter(e.target.value)}
                  />
                </div>
                <div className='col-md-2 d-flex align-items-end'>
                  <button
                    className='btn btn-light w-100'
                    type='button'
                    onClick={() => {
                      setDescriptionFilter('')
                    }}
                  >
                    Limpar
                  </button>
                </div>
              </div>

              <Datatable
                data={formattedData}
                headers={[
                  // {key: 'ID', value: 'id', type: 'default'},
                  {key: 'Descrição', value: 'saleReasonCode', type: 'default'},
                  {key: 'Criado em', value: 'createdAt', type: 'default'},
                  {key: 'Ações', value: 'acoes', type: ''},
                ]}
                options={[
                  {
                    icon: 'fas fa-pen',
                    action: (row: any) => navigate(`/seller/reason/edit/${row.id}`),
                    title: 'Editar',
                  },
                  ...(canDelete
                    ? [
                        {
                          icon: 'fas fa-trash',
                          action: (row: any) => handleDelete(row.id),
                          title: 'Excluir',
                        },
                      ]
                    : []),
                ]}
                color='primary'
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const SellerNoSaleReason: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[{title: 'Motivos Não Venda', path: '/seller/reason', isActive: true}]}
      >
        Motivos de Não Venda
      </PageTitle>
      <SellerNoSaleReasonContent />
    </>
  )
}

export {SellerNoSaleReason}
